import React, { useEffect, useId, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Icon from './AppIcon'
import { submitFeatureRequest } from '../lib/featureRequests'
import './FeatureRequestButton.css'

const PAGE_NAMES = [
  ['/artgold-admin', 'ArtGold Admin'], ['/timesheet-review', 'Team management'],
  ['/timesheet', 'My timesheet'], ['/exchange-desk', 'Exchange Desk'],
  ['/product-listing', 'Product Listing'], ['/jobs', 'Jobs'],
  ['/settings', 'Settings'], ['/home', 'Home'],
]

function currentContext(location) {
  const selected = [...document.querySelectorAll('main nav button[aria-current="page"], main [role="tab"][aria-selected="true"]')]
    .find((element) => element.getClientRects().length > 0)
  const queryTab = new URLSearchParams(location.search).get('tab') || ''
  const tab = selected?.textContent?.trim().replace(/\s+/g, ' ').slice(0, 80)
    || (/^[a-zA-Z0-9_-]{1,80}$/.test(queryTab) ? queryTab : '')
  return { page: location.pathname, tab,
    label: PAGE_NAMES.find(([path]) => location.pathname.startsWith(path))?.[1] || 'ArtGold Portal' }
}

function RequestDialog({ context, onClose, onSubmit }) {
  const titleId = useId(), descriptionId = useId()
  const dialog = useRef(null), pending = useRef(null), mounted = useRef(false), submitting = useRef(false)
  const [kind, setKind] = useState('feature')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    mounted.current = true
    const element = dialog.current, previousFocus = document.activeElement
    element.showModal()
    return () => { mounted.current = false; element.close(); previousFocus?.focus?.({ preventScroll: true }) }
  }, [])
  const submit = async (event) => {
    event.preventDefault()
    if (submitting.current) return
    const message = description.trim()
    if (message.length < 10) { setError('Please describe your request in at least 10 characters.'); return }
    const payload = { kind, description: message, page: context.page, tab: context.tab }
    const signature = JSON.stringify(payload)
    if (pending.current?.signature !== signature) pending.current = { signature, requestId: Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('') }
    submitting.current = true; setBusy(true); setError('')
    try {
      const receipt = await onSubmit({ ...payload, requestId: pending.current.requestId })
      if (typeof receipt?.id !== 'string' || !receipt.id) throw new Error('Missing confirmation')
      if (mounted.current) setSaved(true)
    } catch {
      if (mounted.current) setError('Your request was not confirmed. Please try again. Your description is still here.')
    } finally {
      submitting.current = false
      if (mounted.current) setBusy(false)
    }
  }
  return <dialog ref={dialog} className="feature-request-dialog" aria-labelledby={titleId} aria-describedby={descriptionId}
    onCancel={(event) => { event.preventDefault(); if (!busy) onClose() }}>
    <div className="feature-request-heading"><h2 id={titleId}>{saved ? 'Request received' : 'Feature request'}</h2><button type="button" className="feature-request-close" aria-label="Close feature request" disabled={busy} onClick={onClose}><Icon name="X" size={20} /></button></div>
    {saved ? <div className="feature-request-success"><Icon name="CheckCircle2" size={36} aria-hidden="true" /><p id={descriptionId} role="status">Your request was saved for review.</p><button type="button" className="feature-request-primary" autoFocus onClick={onClose}>Done</button></div> : <form onSubmit={submit}>
      <p id={descriptionId} className="feature-request-intro">Tell us what would make your work easier.</p>
      <div className="feature-request-context"><span>Current page</span><strong>{context.label}{context.tab ? ` · ${context.tab}` : ''}</strong><small>{context.page}</small></div>
      <fieldset disabled={busy}>
        <label>Type of request<select value={kind} onChange={(event) => setKind(event.target.value)}><option value="feature">New feature</option><option value="improvement">Improvement</option><option value="issue">Report an issue</option></select></label>
        <label>Description<textarea autoFocus required minLength={10} maxLength={2000} rows={5} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What would you like to add or improve?" /></label>
        <div className="feature-request-field-help"><span>A clear example helps us understand your request.</span><span>{description.length}/2000</span></div>
      </fieldset>
      {error && <p role="alert" className="feature-request-error">{error}</p>}
      <div className="feature-request-actions"><button type="button" className="feature-request-secondary" disabled={busy} onClick={onClose}>Cancel</button><button type="submit" className="feature-request-primary" disabled={busy}>{busy ? 'Saving…' : 'Submit request'}</button></div>
    </form>}
  </dialog>
}

export default function FeatureRequestButton({ user, onSubmit = submitFeatureRequest }) {
  const location = useLocation()
  const [context, setContext] = useState(null)
  useEffect(() => setContext(null), [user?.uid, location.pathname])
  if (!user) return null
  return <>
    <button type="button" className="feature-request-trigger" onClick={() => setContext(currentContext(location))}><Icon name="MessageSquarePlus" size={17} aria-hidden="true" /><span>Feature request</span></button>
    {context && <RequestDialog key={user.uid} context={context} onClose={() => setContext(null)} onSubmit={onSubmit} />}
  </>
}
